import { Component, forwardRef, Input } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from "@angular/forms";
import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import { TypeaheadMatch } from "ngx-bootstrap";
import { DipendenteVO } from "@app/shared/vo/dipendenteVO";
import { OrganoService } from "@app/shared/services/organo.service";

@Component({
  selector: "app-autocomplete-dipendente",
  templateUrl: "./autocomplete-dipendente.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDipendenteComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AutocompleteDipendenteComponent),
      multi: true,
    },
  ],
})
export class AutocompleteDipendenteComponent implements ControlValueAccessor, Validator {
  public typeaheadLoading: boolean;
  public typeaheadNoResults: boolean;
  public asyncDipSelected: string;
  public dataSourceDip: Observable<any>;
  public readOnly = false;
  public dipendente: DipendenteVO;

  @Input()
  labelDip: string;

  public onChange: (value: DipendenteVO | null) => void = () => {};
  public onTouched: () => void = () => {};

  constructor(private organoService: OrganoService) {
    this.asyncDipSelected = "";
    this.dataSourceDip = new Observable((observer: any) => {
      observer.next(this.asyncDipSelected);
    }).pipe(
      switchMap((token: string) => this.organoService.getDipendenti(String(token)))
    );
  }

  writeValue(value: DipendenteVO | null): void {
    this.setDipendente(value);
  }

  registerOnChange(fn: (value: DipendenteVO | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled;
  }

  public changeTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  public changeTypeaheadDipNoResults(e: boolean): void {
    this.typeaheadNoResults = e;
  }

  public typeaheadDipOnSelect(e: TypeaheadMatch): void {
    const dipendente = this.impostaDipendente(e.item);
    this.onChange(dipendente);
    this.onTouched();
  }

  public onInputChange(value: string): void {
    this.asyncDipSelected = value;

    if (!this.dipendente) {
      this.onValidatorChange();
      return;
    }

    if (value !== this.formatDipendente(this.dipendente)) {
      this.dipendente = null;
      this.onChange(null);
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.asyncDipSelected && !this.dipendente) {
      return { nonTrovato: true };
    }
    return null;
  }

  public onBlur(): void {
    if (!this.dipendente && !this.asyncDipSelected) {
      this.onChange(null);
    }

    this.onTouched();
  }

  public setDipendente(dipendente: DipendenteVO | null): void {
    this.dipendente = dipendente ? this.normalizzaDipendente(dipendente) : null;
    this.asyncDipSelected = this.dipendente ? this.dipendente.output : "";
  }

  private impostaDipendente(dipendente: DipendenteVO): DipendenteVO {
    this.setDipendente(dipendente);
    return this.dipendente;
  }

  private normalizzaDipendente(dipendente: DipendenteVO): DipendenteVO {
    return {
      ...dipendente,
      output: this.formatDipendente(dipendente),
    };
  }

  private formatDipendente(dipendente: DipendenteVO): string {
    if (!dipendente) {
      return "";
    }

    let output = [dipendente.nome, dipendente.cognome].filter(Boolean).join(" ").trim();

    if (dipendente.cmu) {
      output += " - " + dipendente.cmu;
    }

    return output;
  }

  private onValidatorChange: () => void = () => {};

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}
